import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    apiSaveSCurveBenchmark,
    type SCurveRow,
    apiRunSCurve,
    type RunSCurveMeta,
    ForecastModel,
} from '../api/sCurve.api';
import { apiGetCurrentList, listDraftItems, apiSaveDraftBatch } from 'src/features/wbs/editor/api';
import { buildDraftTreeIndex } from '../lib/wbsTree';
import {
    toISODateOrNull,
    getRangeOf,
    computeActionStateByKey,
    applyShiftDraft,
    applyExtendFinishDraft,
    applyExtendStartDraft,
} from '../lib/sCurveDraftEdit';
import { keyOf } from 'src/features/wbs/editor/types.local';
import { WbsItemVersion, DraftItem } from 'src/features/wbs/editor/types.local';
import { formatProjectDateOnly, toProjectDateOnly } from 'src/utils/date';
import { useProjectSetting } from 'src/features/projects/hooks/useProjectSetting';
import { useProjectAccessCtx } from 'src/features/projects/ProjectAccessContext';
import { hasPerm } from 'src/features/projects/hooks/useProjectAccess';

import SCurveChart from '../components/SCurveChart';
import SCurveWbsList, { type SCurveWbsRow } from '../components/SCurveWbsList';

import { useToast } from 'src/shared/ui/toast/useToast';

type SourceType = 'CURRENT' | 'DRAFT';

type OriginalMeta = {
    plannedStart: string | null;
    plannedFinish: string | null;
    weight: number | null; // 0~100
};

type ProjectCurvePreset =
    | 'PROJECT_SETTINGS'
    | 'ENGINEERING'
    | 'PROCUREMENT'
    | 'RND'
    | 'CONSTRUCTION'
    | 'IT_AGILE'
    | 'MAINTENANCE'
    | 'CUSTOM';

const PRESETS: Record<ProjectCurvePreset, { label: string; alpha: number; beta: number }> = {
    PROJECT_SETTINGS: { label: 'Project Settings (DB)', alpha: 2.2, beta: 2.2 }, // 실제값은 dbAlpha/dbBeta 사용
    ENGINEERING: { label: 'Engineering', alpha: 2.2, beta: 2.2 },
    PROCUREMENT: { label: 'Procurement', alpha: 1.5, beta: 3.0 },
    RND: { label: 'R&D', alpha: 1.2, beta: 3.8 },
    CONSTRUCTION: { label: 'Construction', alpha: 2.6, beta: 1.8 },
    IT_AGILE: { label: 'IT Agile', alpha: 1.8, beta: 2.6 },
    MAINTENANCE: { label: 'Maintenance', alpha: 2.0, beta: 2.0 },
    CUSTOM: { label: 'Custom', alpha: 2.2, beta: 2.2 },
};

function round2(n: number) {
    return Math.round(n * 100) / 100;
}

function toNumOrNull(v: unknown): number | null {
    if (v == null) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

function toIso10(s: string | null | undefined) {
    if (!s) return null;
    return formatProjectDateOnly(s, 'Asia/Seoul', '') || null;
}

// YYYY-MM-DD -> ms
function dateMs(iso10: string) {
    // Date(YYYY-MM-DD)는 로컬/UTC 차이가 생길 수 있어 UTC로 고정
    const [y, m, d] = iso10.split('-').map(Number);
    return Date.UTC(y, (m ?? 1) - 1, d ?? 1);
}

/**
 * ✅ 일 단위 균등 분배 + "금요일 라벨(토~금)" 주간 bin에 합산
 * - 하루 증가분은 "그날 EOD" 기준으로 반영(=해당 날짜가 속한 금요일 라벨 bin에 들어감)
 * - start/finish 모두 포함
 * - 주 표시는 금요일(scRows.date = Friday) 기준
 */
function buildDailyUniformIncSeries(params: {
    dates: string[]; // scRows date (YYYY-MM-DD) - Fridays
    start: string; // YYYY-MM-DD
    finish: string; // YYYY-MM-DD
    weight: number; // 0~100
}) {
    const { dates, start, finish, weight } = params;

    const s = toIso10(start);
    const f = toIso10(finish);
    if (!s || !f || !Number.isFinite(weight) || weight <= 0) {
        return new Array(dates.length).fill(0);
    }

    const n = dates.length;
    const out = new Array(n).fill(0);
    if (n === 0) return out;

    const dayMs = 86400000;

    // ✅ scRows Friday date -> index 맵
    const fridayIndex = new Map<string, number>();
    for (let i = 0; i < n; i++) fridayIndex.set(dates[i], i);

    // ✅ start/finish (UTC, 날짜 00:00 기준)
    const sMs = dateMs(s);
    const fMs = dateMs(f);
    const a = Math.min(sMs, fMs);
    const b = Math.max(sMs, fMs);

    const startDay = Math.floor(a / dayMs) * dayMs;
    const finishDay = Math.floor(b / dayMs) * dayMs;

    // ✅ inclusive day count (start/finish 모두 포함)
    const totalDays = Math.max(1, Math.round((finishDay - startDay) / dayMs) + 1);
    const dailyInc = weight / totalDays;

    // ✅ 각 날짜 d가 속한 "금요일 라벨(토~금)"을 구해서 해당 bin에 dailyInc를 더함
    // JS UTC day: 0=Sun,1=Mon,...,5=Fri,6=Sat
    // deltaToFri = (5 - dow + 7) % 7
    for (let k = 0; k < totalDays; k++) {
        const dayStart = startDay + k * dayMs;
        const d = new Date(dayStart);
        const dow = d.getUTCDay();

        const deltaToFri = (5 - dow + 7) % 7; // Sat(6)->6, Sun(0)->5, Fri(5)->0
        const friMs = dayStart + deltaToFri * dayMs;

        const friIso = toIso10(new Date(friMs).toISOString())!;
        const idx = fridayIndex.get(friIso);
        if (idx == null) continue; // scRows 범위 밖이면 skip

        out[idx] += dailyInc;
    }

    return out;
}

function ensureDraftKeys(items: DraftItem[]): DraftItem[] {
    return items.map((it, idx) => {
        const nodeId = typeof it.nodeId === 'string' && it.nodeId.length > 0 ? it.nodeId : null;
        const cid = (it as any).cid as string | undefined;

        // ✅ Batch 저장 API는 clientId가 필수.
        // - 서버에서 내려오는 draft(listDraftItems)는 cid가 없을 수 있으므로 nodeId를 cid로 사용
        // - 신규(아직 nodeId 없음)는 임시 cid 생성
        const nextCid = typeof cid === 'string' && cid.length > 0 ? cid : (nodeId ?? `tmp_${idx}_${Date.now()}`);

        if (typeof cid === 'string' && cid.length > 0) return it;
        return { ...(it as any), cid: nextCid } as DraftItem;
    });
}

function normalizeDraftDateOnly(items: DraftItem[], projectTz: string): DraftItem[] {
    return items.map((it) => {
        const ps = (it as any).plannedStart ?? null;
        const pf = (it as any).plannedFinish ?? null;

        return {
            ...(it as any),
            plannedStart: ps ? toProjectDateOnly(ps, projectTz) : null, // ✅ work items도 YYYY-MM-DD로 고정
            plannedFinish: pf ? toProjectDateOnly(pf, projectTz) : null, // ✅
        } as DraftItem;
    });
}

function mapPublishedItemsToRows(items: WbsItemVersion[], projectTz: string): SCurveWbsRow[] {
    return items.map((it) => ({
        key: it.nodeId,
        nodeId: it.nodeId,
        parentNodeId: it.parentNodeId ?? null,
        code: (it as any).code ?? null,
        name: it.name ?? '(unnamed)',
        orderIndex: (it as any).orderIndex ?? null,
        level: (it as any).level ?? null,
        // plannedStart: it.plannedStart ? it.plannedStart.slice(0, 10) : null,
        plannedStart: it.plannedStart ? toProjectDateOnly(it.plannedStart, projectTz) : null,
        // plannedFinish: it.plannedFinish ? it.plannedFinish.slice(0, 10) : null,
        plannedFinish: it.plannedFinish ? toProjectDateOnly(it.plannedFinish, projectTz) : null,
        weight: (it.weight ?? null) as number | null, // 0~100
    }));
}

function mapDraftItemsToRows(items: DraftItem[], projectTz: string): SCurveWbsRow[] {
    return items.map((it) => {
        const k = keyOf(it);
        const nodeId = typeof it.nodeId === 'string' && it.nodeId.length > 0 ? it.nodeId : k;

        return {
            key: k,
            nodeId,
            parentNodeId: (it as any).parentNodeId ?? null,
            code: (it as any).code ?? null,
            name: (it as any).name ?? '(unnamed)',
            orderIndex: (it as any).orderIndex ?? null,
            level: (it as any).level ?? null,
            // plannedStart: toISODateOrNull((it as any).plannedStart),
            plannedStart: it.plannedStart ? toProjectDateOnly(it.plannedStart, projectTz) : null,
            // plannedFinish: toISODateOrNull((it as any).plannedFinish),
            plannedFinish: it.plannedFinish ? toProjectDateOnly(it.plannedFinish, projectTz) : null,
            weight: toNumOrNull((it as any).weight),
        };
    });
}

export default function SCurveContainer({ projectId }: { projectId: string }) {
    const toast = useToast();
    const { access, isLoading: accessLoading } = useProjectAccessCtx();

    const [source, setSource] = useState<'CURRENT' | 'DRAFT'>('CURRENT');

    const [loadingWbs, setLoadingWbs] = useState(false);
    const [running, setRunning] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const [scRows, setScRows] = useState<SCurveRow[]>([]);
    const [metaMsg, setMetaMsg] = useState<string | null>(null);
    const [runMeta, setRunMeta] = useState<RunSCurveMeta | null>(null);

    const [forecastModel, setForecastModel] = useState<ForecastModel>('EVM_SPI_TIME_EAC');

    const [publishedItems, setPublishedItems] = useState<WbsItemVersion[] | null>(null);
    const [draftBaseItems, setDraftBaseItems] = useState<DraftItem[]>([]);
    const [draftWorkItems, setDraftWorkItems] = useState<DraftItem[] | null>(null);

    const [editMode, setEditMode] = useState(false);

    const { data: psRes } = useProjectSetting(projectId);
    const projectTz = psRes?.data.timeZone ?? 'Asia/Seoul';

    // ✅ 권한 설정
    const canSaveProjectDefault =
        !accessLoading && (access?.roleName === 'PROJECT_OWNER' || access?.roleName === 'PROJECT_MANAGER');
    const canEditWbsDraft = !accessLoading && hasPerm(access?.permissions, 'wbs.edit');
    const editable = source === 'DRAFT' && editMode && canEditWbsDraft;

    // ✅ Draft 사전 점검(합의): plannedStart/plannedFinish 누락/역전이면 Analysis 차단
    const [analysisBlocked, setAnalysisBlocked] = useState<{ blocked: boolean; message: string | null }>({
        blocked: false,
        message: null,
    });

    const NO_CURRENT_WBS_MSG = '아직 분석할 WBS가 없습니다. WBS창에서 WBS를 먼저 입력하세요.';


    /* 벤치마크 관련 변수 */
    const [showBenchmark, setShowBenchmark] = useState(true);
    const [seriesVisible, setSeriesVisible] = useState({
        pvCum: true,
        // TODO: 예측은 일단 끔. 예측 모델 정교하게 만든 후 다시 살릴 것.
        pvForecastCum: false,
        evApprovedCum: true,
        evLatestCum: true, // UI로 제어 (기본 true 추천. 원하면 false로)
        benchCum: true,
        pvInc: true,
        evApprovedInc: true,
    });

    const [showLatest, setShowLatest] = useState(false); // "잠정(EV)" 표시 여부 (기본 false 유지)
    const [overlayVisible, setOverlayVisible] = useState<Record<string, boolean>>({
        overallBell: true,
        selectedBell: true,
    });

    const [preset, setPreset] = useState<ProjectCurvePreset>('ENGINEERING');
    const [alpha, setAlpha] = useState<number>(PRESETS.ENGINEERING.alpha);
    const [beta, setBeta] = useState<number>(PRESETS.ENGINEERING.beta);
    const [dbAlpha, setDbAlpha] = useState<number | null>(null);
    const [dbBeta, setDbBeta] = useState<number | null>(null);
    const [customAlpha, setCustomAlpha] = useState<number>(PRESETS.ENGINEERING.alpha);
    const [customBeta, setCustomBeta] = useState<number>(PRESETS.ENGINEERING.beta);
    const [chartHeight, setChartHeight] = useState<number>(420);
    const [chartCollapsed, setChartCollapsed] = useState<boolean>(false);

    const [selectedKey, setSelectedKey] = useState<string | null>(null);

    const ssKey = useMemo(() => (k: string) => `sCurve:${projectId}:${k}`, [projectId]);

    // 저장/재실행 시점 제어용 ref
    const analysisBlockedRef = useRef(analysisBlocked);

    useEffect(() => {
        analysisBlockedRef.current = analysisBlocked;
    }, [analysisBlocked]);

    const hasDraftChanges = useMemo(() => {
        if (source !== 'DRAFT') return false;
        const base = draftBaseItems ?? [];
        const work = draftWorkItems ?? [];

        const baseByKey = new Map<string, DraftItem>();
        for (const it of base) baseByKey.set(keyOf(it), it);

        const workByKey = new Map<string, DraftItem>();
        for (const it of work) workByKey.set(keyOf(it), it);

        // ✅ 삭제/추가 감지
        if (baseByKey.size !== workByKey.size) return true;
        for (const k of baseByKey.keys()) {
            if (!workByKey.has(k)) return true;
        }

        // ✅ 일정/weight 변경 감지
        for (const [k, w] of workByKey) {
            const b = baseByKey.get(k);
            if (!b) return true;

            const bStart = toISODateOrNull((b as any).plannedStart ?? null);
            const bFinish = toISODateOrNull((b as any).plannedFinish ?? null);
            const bWeight = toNumOrNull((b as any).weight);

            const wStart = toISODateOrNull((w as any).plannedStart ?? null);
            const wFinish = toISODateOrNull((w as any).plannedFinish ?? null);
            const wWeight = toNumOrNull((w as any).weight);

            if (bStart !== wStart) return true;
            if (bFinish !== wFinish) return true;
            if (bWeight !== wWeight) return true;
        }

        return false;
    }, [source, draftBaseItems, draftWorkItems]);

    const canSaveDraft = editable && !saving && !loadingWbs && !analysisBlocked.blocked && hasDraftChanges;

    const buildIndex = (items: DraftItem[]) =>
        buildDraftTreeIndex(items, {
            getKey: keyOf,
            getNodeId: (it) => it.nodeId ?? undefined,
            getParentNodeId: (it) => (it as any).parentNodeId ?? null,
            getClientId: (it) => (it as any).cid ?? undefined,
            getParentClientId: (it) => (it as any).parentClientId ?? null,
        });

    const loadWbs = useCallback(async () => {
        setErr(null);
        setLoadingWbs(true);
        setMetaMsg(null);
        setAnalysisBlocked({ blocked: false, message: null });

        try {
            if (source === 'CURRENT') {
                const res: any = await apiGetCurrentList(projectId);
                const items: WbsItemVersion[] = (res?.items ?? res?.list?.items ?? []) as WbsItemVersion[];
                const nextItems = Array.isArray(items) ? items : [];

                setPublishedItems(nextItems);

                if (nextItems.length === 0) {
                    setScRows([]);
                    setRunMeta(null);
                    setErr(NO_CURRENT_WBS_MSG);
                }
            } else {
                const raw = await listDraftItems(projectId);
                // const drafts = ensureDraftKeys(Array.isArray(raw) ? raw : []);
                const drafts0 = ensureDraftKeys(Array.isArray(raw) ? raw : []);
                const drafts = normalizeDraftDateOnly(drafts0, projectTz); // ✅ 추가

                // ✅ 사전 점검: 누락/역전 존재 시 차단
                const invalid = drafts.map((it) => ({ k: keyOf(it), r: getRangeOf(it), it })).filter((x) => !x.r);

                if (invalid.length > 0) {
                    setDraftBaseItems(drafts);
                    setDraftWorkItems(drafts);
                    setAnalysisBlocked({
                        blocked: true,
                        message: `WBS Draft에 plannedStart/plannedFinish 누락 또는 start>finish 항목이 있습니다. (총 ${invalid.length}개)\nAnalysis를 하기 전에 WBS를 먼저 수정하고 오세요.`,
                    });
                } else {
                    setDraftBaseItems(drafts);
                    setDraftWorkItems(drafts);
                }
            }
        } catch (e: any) {
            setErr(e?.message ?? 'WBS 로딩 실패');
        } finally {
            setLoadingWbs(false);
        }
    }, [projectId, source]);

    useEffect(() => {
        if (source === 'CURRENT') setEditMode(false);
        loadWbs();
    }, [source, loadWbs, projectId]);

    const runSCurve = useCallback(async () => {
        setErr(null);
        setMetaMsg(null);
        if (analysisBlocked.blocked) return;

        setRunning(true);
        try {
            // ✅ run() 내부, apiRunSCurve 호출 직전에 추가
            const draftPatch =
                source === 'DRAFT'
                    ? (draftWorkItems ?? []).map((it) => ({
                          key: keyOf(it),
                          nodeId: it.nodeId ?? null,
                          plannedStart: toISODateOrNull((it as any).plannedStart ?? null),
                          plannedFinish: toISODateOrNull((it as any).plannedFinish ?? null),
                          weight: toNumOrNull((it as any).weight),
                      }))
                    : undefined;

            // ✅ apiRunSCurve 호출부에서 draftPatch 전달
            const out = await apiRunSCurve(projectId, {
                source: source === 'DRAFT' ? { type: 'DRAFT' } : { type: 'CURRENT' },
                timeUnit: 'WEEK',
                allocationMethod: 'LINEAR',
                // forecastModel: 'LEGACY',
                forecastModel,
                // ✅ draftPatch가 있는 경우에만 전달(기존 draftPatch 생성 로직 재사용)
                draftPatch: draftPatch?.length ? draftPatch : undefined,
            });

            setScRows(out.rows);
            setRunMeta(out.meta);

            const warnings: string[] = [];
            const invalidCount = out.meta.invalidCount ?? 0;
            if (invalidCount > 0) warnings.push(`유효하지 않은 항목 ${invalidCount}개 제외됨`);
            if (warnings.length) setMetaMsg(warnings.join(' · '));
        } catch (e: any) {
            const status = e?.response?.status;
            const code = e?.response?.data?.code;
            const serverMessage = e?.response?.data?.message;

            if (status === 404 || code === 'WBS_CURRENT_NOT_FOUND' || serverMessage === 'No current WBS list found.') {
                setScRows([]);
                setRunMeta(null);
                setErr(NO_CURRENT_WBS_MSG);
            } else {
                setErr(serverMessage ?? e?.message ?? 'S-curve 계산 실패');
            }
        } finally {
            setRunning(false);
        }
    }, [analysisBlocked.blocked, draftWorkItems, projectId, source, forecastModel]);

    // ✅ 1회 자동 실행 플래그
    const didAutoRunRef = useRef(false);

    // projectId / source 바뀌면 다시 1회 자동 실행 가능
    useEffect(() => {
        didAutoRunRef.current = false;
        setScRows([]); // 선택: 소스 전환 시 이전 차트 잔상 제거
    }, [projectId, source]);

    // ✅ WBS 로딩 완료 후 자동 run (Published 기본 진입 시 차트가 바로 뜸)
    useEffect(() => {
        if (didAutoRunRef.current) return;
        if (loadingWbs) return;
        if (running) return;
        if (analysisBlocked.blocked) return;

        // CURRENT: publishedItems가 로딩된 뒤 실행
        if (source === 'CURRENT') {
            if (!publishedItems) return; // 아직 미로딩
            if (publishedItems.length === 0) {
                didAutoRunRef.current = true;
                return;
            }
        }

        // DRAFT: draftWorkItems 로딩된 뒤 실행
        if (source === 'DRAFT') {
            if (!draftWorkItems) return;
        }

        didAutoRunRef.current = true;
        runSCurve();
    }, [source, loadingWbs, running, analysisBlocked.blocked, publishedItems, draftWorkItems, runSCurve]);

    const saveDraftBatch = useCallback(async () => {
        if (!editable) return;
        if (analysisBlocked.blocked) return;
        if (!hasDraftChanges) return;

        const items = draftWorkItems ?? [];
        if (!items.length) {
            toast.warning('저장할 Draft WBS가 없습니다.', {
                title: '저장 불가',
            });
            return;
        }

        const ok = window.confirm(
            [
                '⚠️ Draft WBS 일괄 저장',
                '',
                '이 작업은 현재 화면에서 편집 중인 Draft WBS를 데이터베이스에 즉시 반영합니다.',
                '저장된 내용은 자동으로 되돌릴 수 없으며, 다른 사용자/화면에도 바로 영향을 줄 수 있습니다.',
                '',
                '계속 진행하시겠습니까?',
            ].join('\n'),
        );
        if (!ok) return;

        setSaving(true);
        setErr(null);
        setMetaMsg(null);

        try {
            const payload = items.map((it, idx) => {
                const cid = ((it as any).cid as string | undefined) ?? it.nodeId ?? `tmp_${idx}`;
                const parentClientId = (it as any).parentClientId as string | undefined;

                return {
                    clientId: cid,
                    nodeId: it.nodeId ?? null,
                    parentNodeId: (it as any).parentNodeId ?? null,
                    // parentClientId: (it as any).parentClientId ?? null,
                    ...(parentClientId ? { parentClientId } : {}), // parentClientId가 없으면 null을 보내지 말고 아예 key를 없앤다.
                    orderIndex: (it as any).orderIndex ?? idx,
                    name: it.name,
                    plannedStart: toISODateOrNull((it as any).plannedStart ?? null), // YYYY-MM-DD
                    plannedFinish: toISODateOrNull((it as any).plannedFinish ?? null), // YYYY-MM-DD
                    weight: toNumOrNull((it as any).weight), // 0~100
                    memo: (it as any).memo ?? null,
                };
            });

            await apiSaveDraftBatch(projectId, payload);

            // ✅ 저장 후 DB 기준으로 Draft 재로딩
            await loadWbs();

            // ✅ 재로딩 직후 자동 재실행(상태 반영을 위해 다음 tick)
            setTimeout(() => {
                if (!analysisBlockedRef.current.blocked) {
                    runSCurve();
                }
            }, 0);

            setMetaMsg(`Draft ${payload.length}개 저장 완료`);
        } catch (e: any) {
            const msg =
                e?.response?.data?.error?.message ||
                (e?.response?.data?.error?.issues ? 'ValidationError' : null) ||
                e?.message ||
                'Draft 저장 실패';
            setErr(msg);
        } finally {
            setSaving(false);
        }
    }, [editable, analysisBlocked.blocked, hasDraftChanges, draftWorkItems, projectId, loadWbs, runSCurve]);

    const persistSession = useCallback(
        (next?: Partial<Record<string, any>>) => {
            try {
                const state = {
                    preset,
                    alpha,
                    beta,
                    customAlpha,
                    customBeta,
                    showBenchmark,
                    chartHeight,
                    chartCollapsed,
                    ...(next ?? {}),
                };
                sessionStorage.setItem(ssKey('ui'), JSON.stringify(state));
            } catch {
                // ignore
            }
        },
        [alpha, beta, chartHeight, customAlpha, customBeta, preset, showBenchmark, chartCollapsed, ssKey],
    );

    const onChangePreset = useCallback(
        (p: ProjectCurvePreset) => {
            if (preset === 'CUSTOM') {
                setCustomAlpha(alpha);
                setCustomBeta(beta);
            }

            setPreset(p);

            if (p === 'CUSTOM') {
                setAlpha(customAlpha);
                setBeta(customBeta);
                persistSession({ preset: p, alpha: customAlpha, beta: customBeta });
                return;
            }

            if (p === 'PROJECT_SETTINGS') {
                if (dbAlpha != null && dbBeta != null) {
                    setAlpha(dbAlpha);
                    setBeta(dbBeta);
                    persistSession({ preset: p, alpha: dbAlpha, beta: dbBeta });
                } else {
                    persistSession({ preset: p });
                }
                return;
            }

            setAlpha(PRESETS[p].alpha);
            setBeta(PRESETS[p].beta);
            persistSession({ preset: p, alpha: PRESETS[p].alpha, beta: PRESETS[p].beta });
        },
        [alpha, beta, customAlpha, customBeta, dbAlpha, dbBeta, persistSession, preset],
    );

    const onChangeAlpha = useCallback(
        (next: number) => {
            const v = round2(next);
            setAlpha(v);
            setCustomAlpha(v);
            setPreset('CUSTOM');
            persistSession({ preset: 'CUSTOM', alpha: v, customAlpha: v });
        },
        [persistSession],
    );

    const onChangeBeta = useCallback(
        (next: number) => {
            const v = round2(next);
            setBeta(v);
            setCustomBeta(v);
            setPreset('CUSTOM');
            persistSession({ preset: 'CUSTOM', beta: v, customBeta: v });
        },
        [persistSession],
    );

    const onChangeHeight = useCallback(
        (h: number) => {
            const v = Math.max(240, Math.min(900, Math.round(h)));
            setChartHeight(v);
            persistSession({ chartHeight: v });
        },
        [persistSession],
    );

    const onSaveAsProjectDefault = useCallback(async () => {
        await apiSaveSCurveBenchmark(projectId, alpha, beta);
        setDbAlpha(alpha);
        setDbBeta(beta);
        persistSession();
    }, [alpha, beta, persistSession, projectId]);

    const toggleChartCollapsed = useCallback(() => {
        setChartCollapsed((v) => {
            const next = !v;
            persistSession({ chartCollapsed: next });
            return next;
        });
    }, [persistSession]);

    const wbsRows: SCurveWbsRow[] = useMemo(() => {
        if (source === 'DRAFT') return mapDraftItemsToRows(draftWorkItems ?? [], projectTz);
        return mapPublishedItemsToRows(publishedItems ?? [], projectTz);
    }, [source, draftWorkItems, publishedItems, projectTz]);

    // ✅ 전체 WBS bell curve (leaf 기준으로 합산)
    const overallBellOverlay = useMemo(() => {
        if (!scRows?.length) return null;
        if (!wbsRows?.length) return null;

        // scRows 날짜축
        const dates = scRows.map((r) => toIso10((r as any).date) ?? '');
        const safeDates = dates.filter(Boolean);
        if (safeDates.length !== scRows.length) return null;

        // ✅ leaf 판정: "누군가의 parentNodeId로 등장하는 nodeId"는 부모 => leaf 제외
        const parentIds = new Set<string>();
        for (const r of wbsRows) {
            if (r.parentNodeId) parentIds.add(r.parentNodeId);
        }

        const sum = new Array(scRows.length).fill(0);

        for (const r of wbsRows) {
            // ✅ leaf만 합산(중복합산 방지)
            if (parentIds.has(r.nodeId)) continue;

            const start = toIso10(r.plannedStart);
            const finish = toIso10(r.plannedFinish);
            const weight = r.weight ?? 0;

            if (!start || !finish || !Number.isFinite(weight) || weight <= 0) continue;

            const inc = buildDailyUniformIncSeries({ dates: safeDates, start, finish, weight });
            for (let i = 0; i < sum.length; i++) sum[i] += inc[i];
        }

        return {
            id: 'overallBell',
            label: '전체 WBS 가중치 분포',
            values: sum,
            axis: 'inc' as const,
        };
    }, [scRows, wbsRows]);

    // leaf 자손들만 더해서 환산하는 방법
    const selectedBellOverlay = useMemo(() => {
        if (!selectedKey) return null;
        if (!scRows?.length) return null;
        if (!wbsRows?.length) return null;

        const selected = wbsRows.find((r) => r.key === selectedKey);
        if (!selected) return null;

        const dates = scRows.map((r) => toIso10((r as any).date) ?? '');
        const safeDates = dates.filter(Boolean);
        if (safeDates.length !== scRows.length) return null;

        // 1) parent -> children 인덱스 만들기
        const childrenByParent = new Map<string, string[]>();
        for (const r of wbsRows) {
            if (!r.parentNodeId) continue;
            const arr = childrenByParent.get(r.parentNodeId) ?? [];
            arr.push(r.nodeId);
            childrenByParent.set(r.parentNodeId, arr);
        }

        // 2) selected의 모든 자손 nodeId 수집
        const descendantIds = new Set<string>([selected.nodeId]);
        const stack: string[] = [selected.nodeId];

        while (stack.length) {
            const cur = stack.pop()!;
            const kids = childrenByParent.get(cur) ?? [];
            for (const kid of kids) {
                if (descendantIds.has(kid)) continue;
                descendantIds.add(kid);
                stack.push(kid);
            }
        }

        // ✅ "자손 중 leaf" = 자손인데 children이 없는 노드
        const sum = new Array(scRows.length).fill(0);

        for (const r of wbsRows) {
            if (!descendantIds.has(r.nodeId)) continue;

            const hasChildren = (childrenByParent.get(r.nodeId)?.length ?? 0) > 0;
            if (hasChildren) continue; // leaf만

            const start = toIso10(r.plannedStart);
            const finish = toIso10(r.plannedFinish);
            const weight = r.weight ?? 0;

            if (!start || !finish || !Number.isFinite(weight) || weight <= 0) continue;

            const inc = buildDailyUniformIncSeries({ dates: safeDates, start, finish, weight });
            for (let i = 0; i < sum.length; i++) sum[i] += inc[i];
        }

        // leaf 자손이 하나도 없으면 null (표시 안 함)
        const any = sum.some((v) => v > 0);
        if (!any) return null;

        return {
            id: 'selectedBell',
            label: `선택 WBS 가중치 분포`,
            values: sum,
            axis: 'inc' as const,
        };
    }, [selectedKey, scRows, wbsRows]);

    const originalByKey = useMemo(() => {
        if (source !== 'DRAFT') return undefined;
        const m = new Map<string, OriginalMeta>();
        for (const it of draftBaseItems ?? []) {
            const k = keyOf(it);
            m.set(k, {
                // plannedStart: toISODateOrNull((it as any).plannedStart),
                plannedStart: it.plannedStart ? toProjectDateOnly(it.plannedStart, projectTz) : null,
                // plannedFinish: toISODateOrNull((it as any).plannedFinish),
                plannedFinish: it.plannedFinish ? toProjectDateOnly(it.plannedFinish, projectTz) : null,
                weight: toNumOrNull((it as any).weight),
            });
        }
        return m;
    }, [source, draftBaseItems, projectTz]);

    const actionStateByKey = useMemo(() => {
        if (source !== 'DRAFT') return undefined;

        const items = draftWorkItems ?? [];
        const idx = buildIndex(items);

        return computeActionStateByKey(items, idx, keyOf);
    }, [source, draftWorkItems]);

    const resetDraftWorkItems = useCallback(() => {
        if (source !== 'DRAFT') return;
        if (!draftBaseItems) return;
        setDraftWorkItems(draftBaseItems);
        setErr(null);
        setMetaMsg(null);
    }, [source, draftBaseItems]);

    const canReset = source === 'DRAFT' && !!draftBaseItems && (draftWorkItems ?? []).length > 0;

    const onEditField = useCallback(
        (rowKey: string, patch: Partial<Pick<SCurveWbsRow, 'plannedStart' | 'plannedFinish' | 'weight'>>) => {
            if (source !== 'DRAFT') return;
            if (analysisBlocked.blocked) return;

            setDraftWorkItems((prev) => {
                const items = (prev ?? []) as any[];
                const idx = buildIndex(items as any);

                const me = idx.byKey.get(rowKey);
                if (!me) return items as any;

                const next = { ...me };

                if ('plannedStart' in patch) next.plannedStart = patch.plannedStart ?? null;
                if ('plannedFinish' in patch) next.plannedFinish = patch.plannedFinish ?? null;
                if ('weight' in patch) next.weight = patch.weight ?? null;

                const nr = getRangeOf(next);
                if (!nr) {
                    setErr('날짜가 유효하지 않습니다(start/finish 누락 또는 start>finish).');
                    return items as any;
                }

                const parentKey = idx.keyToParentKey.get(rowKey) ?? null;
                if (parentKey) {
                    const parent = idx.byKey.get(parentKey);
                    const pr = getRangeOf(parent);

                    if (!pr) {
                        setErr('부모 일정(plannedStart/Finish)이 유효하지 않습니다. WBS를 먼저 수정하세요.');
                        return items as any;
                    }

                    // 부모 경계 체크
                    if (nr.start < pr.start || nr.finish > pr.finish) {
                        setErr('부모 일정 범위를 벗어날 수 없습니다.');
                        return items as any;
                    }
                }

                // 적용
                const copied = items.slice();
                const i = copied.findIndex((x) => keyOf(x) === rowKey);
                if (i >= 0) copied[i] = next;
                return copied as any;
            });
        },
        [source, analysisBlocked.blocked],
    );

    const onShift = useCallback(
        (rowKey: string, dir: 'BACK' | 'FWD') => {
            if (source !== 'DRAFT') return;
            if (analysisBlocked.blocked) return;

            setDraftWorkItems((prev) => {
                const items = (prev ?? []) as any[];
                const idx = buildIndex(items as any);
                return applyShiftDraft(items, idx, keyOf, rowKey, dir) as any;
            });
        },
        [source, analysisBlocked.blocked],
    );

    const onExtendFinish = useCallback(
        (rowKey: string, dir: 'SHRINK' | 'EXTEND') => {
            if (source !== 'DRAFT') return;
            if (analysisBlocked.blocked) return;

            setDraftWorkItems((prev) => {
                const items = (prev ?? []) as any[];
                const idx = buildIndex(items as any);
                return applyExtendFinishDraft(items, idx, keyOf, rowKey, dir) as any;
            });
        },
        [source, analysisBlocked.blocked],
    );

    const onExtendStart = useCallback(
        (rowKey: string, dir: 'BACK' | 'FWD') => {
            if (source !== 'DRAFT') return;
            if (analysisBlocked.blocked) return;

            setDraftWorkItems((prev) => {
                const items = (prev ?? []) as any[];
                const idx = buildIndex(items as any);
                return applyExtendStartDraft(items, idx, keyOf, rowKey, dir) as any;
            });
        },
        [source, analysisBlocked.blocked],
    );

    // console.log('Forecast Model: ', forecastModel);

    return (
        <div className="h-[calc(100vh-24px)] overflow-hidden">
            <div className="h-full flex flex-col space-y-3">
                {err && (
                    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        {err}
                    </div>
                )}

                {metaMsg && !err && (
                    <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                        {metaMsg}
                    </div>
                )}

                {/* Top: Chart card */}
                <div className="sticky z-20 rounded-2xl border border-slate-200 bg-white p-4">
                    {/* Draft Handling Control */}
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                            <select
                                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                                value={source}
                                onChange={(e) => setSource(e.target.value as SourceType)}
                                disabled={loadingWbs || saving}
                            >
                                <option value="CURRENT">Published (Read-only)</option>
                                <option value="DRAFT">Draft (Editable)</option>
                            </select>
                            {source === 'DRAFT' && (
                                <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={editMode}
                                        onChange={(e) => setEditMode(e.target.checked)}
                                        disabled={!canEditWbsDraft}
                                        title={canEditWbsDraft ? 'Draft 편집 모드' : 'WBS 편집 권한이 없습니다.'}
                                    />
                                    Edit mode
                                </label>
                            )}
                            <button
                                type="button"
                                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50"
                                onClick={loadWbs}
                                disabled={loadingWbs || saving}
                            >
                                Reload WBS
                            </button>

                            <select
                                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                                value={forecastModel}
                                onChange={(e) => setForecastModel(e.target.value as any)}
                                disabled={loadingWbs || saving}
                            >
                                <option value="NONE">Forecast: Off</option>
                                <option value="LEGACY">Forecast: LEGACY</option>
                                <option value="EVM_SPI_TIME_EAC">Forecast: EVM (SPI time EAC)</option>
                            </select>

                            <button
                                type="button"
                                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                onClick={runSCurve}
                                disabled={running || loadingWbs || saving}
                                title="현재 편집 값으로 즉시 계산합니다(서버 호출 없음)"
                            >
                                {running ? 'Running…' : 'Run S-Curve'}
                            </button>
                        </div>
                        <div>
                            {/* {source === 'DRAFT' && ( */}
                            <button
                                type="button"
                                className={[
                                    'h-9 rounded-lg px-3 text-sm',
                                    canSaveDraft
                                        ? 'bg-amber-600 text-white hover:bg-amber-700'
                                        : 'bg-slate-100 text-slate-400 cursor-not-allowed',
                                ].join(' ')}
                                onClick={saveDraftBatch}
                                disabled={!canSaveDraft}
                                title={
                                    analysisBlocked.blocked
                                        ? '분석 차단 상태에서는 저장할 수 없습니다'
                                        : !hasDraftChanges
                                          ? '변경된 내용이 없습니다'
                                          : editable
                                            ? 'Draft 전체를 batch 저장(확정)'
                                            : 'Draft + Edit mode에서만 저장 가능'
                                }
                            >
                                {saving ? 'Saving…' : 'Save Draft (Batch)'}
                            </button>
                            {/* )} */}
                        </div>
                    </div>
                    {/* Benchmark controls (from legacy SCurvePage) */}
                    <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">프로젝트 성격</span>
                                <select
                                    className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                                    value={preset}
                                    onChange={(e) => onChangePreset(e.target.value as ProjectCurvePreset)}
                                    // disabled={analysisBlocked.blocked}
                                >
                                    <option value="PROJECT_SETTINGS">{PRESETS.PROJECT_SETTINGS.label}</option>
                                    <option value="ENGINEERING">{PRESETS.ENGINEERING.label}</option>
                                    <option value="PROCUREMENT">{PRESETS.PROCUREMENT.label}</option>
                                    <option value="RND">{PRESETS.RND.label}</option>
                                    <option value="CONSTRUCTION">{PRESETS.CONSTRUCTION.label}</option>
                                    <option value="IT_AGILE">{PRESETS.IT_AGILE.label}</option>
                                    <option value="MAINTENANCE">{PRESETS.MAINTENANCE.label}</option>
                                    <option value="CUSTOM">{PRESETS.CUSTOM.label}</option>
                                </select>
                            </div>
                            {/* Line Chart 선택 버튼 */}
                            <div className="flex items-center gap-2">
                                <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={showBenchmark}
                                        onChange={(e) => {
                                            const v = e.target.checked;
                                            setShowBenchmark(v);
                                            persistSession({ showBenchmark: v });
                                        }}
                                    />
                                    표준(Benchmark)
                                </label>
                                <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={seriesVisible.pvCum}
                                        onChange={(e) => {
                                            const v = e.target.checked;
                                            setSeriesVisible((s) => ({ ...s, pvCum: v }));
                                            persistSession({ seriesVisible: { ...seriesVisible, pvCum: v } });
                                        }}
                                    />
                                    계획(PV)
                                </label>

                                <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={seriesVisible.evApprovedCum}
                                        onChange={(e) => {
                                            const v = e.target.checked;
                                            setSeriesVisible((s) => ({ ...s, evApprovedCum: v }));
                                            persistSession({ seriesVisible: { ...seriesVisible, evApprovedCum: v } });
                                        }}
                                    />
                                    확정(EV)
                                </label>

                                {/* <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={showLatest}
                                        onChange={(e) => {
                                            const v = e.target.checked;
                                            setShowLatest(v);
                                            persistSession({ showLatest: v });
                                        }}
                                        disabled
                                    />
                                    잠정(EV)
                                </label> */}

                                <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={seriesVisible.pvForecastCum}
                                        onChange={(e) => {
                                            const v = e.target.checked;
                                            setSeriesVisible((s) => ({ ...s, pvForecastCum: v }));
                                            persistSession({ seriesVisible: { ...seriesVisible, pvForecastCum: v } });
                                        }}
                                        disabled
                                    />
                                    예측(EV)
                                </label>

                                <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={seriesVisible.pvInc}
                                        onChange={(e) => setSeriesVisible((s) => ({ ...s, pvInc: e.target.checked }))}
                                    />
                                    계획 증가
                                </label>

                                <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={seriesVisible.evApprovedInc}
                                        onChange={(e) =>
                                            setSeriesVisible((s) => ({ ...s, evApprovedInc: e.target.checked }))
                                        }
                                    />
                                    확정 증가
                                </label>

                                <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={overlayVisible.overallBell !== false}
                                        onChange={(e) => {
                                            const v = e.target.checked;
                                            setOverlayVisible((m) => ({ ...m, overallBell: v }));
                                            persistSession({ overlayVisible: { ...overlayVisible, overallBell: v } });
                                        }}
                                    />
                                    전체 WBS 가중치 분포
                                </label>

                                <label className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={overlayVisible.selectedBell !== false}
                                        onChange={(e) => {
                                            const v = e.target.checked;
                                            setOverlayVisible((m) => ({ ...m, selectedBell: v }));
                                            persistSession({ overlayVisible: { ...overlayVisible, selectedBell: v } });
                                        }}
                                    />
                                    선택 WBS 가중치 분포
                                </label>
                            </div>

                            <div className="ml-auto flex items-center gap-2">
                                <button
                                    type="button"
                                    className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                                    onClick={async () => {
                                        if (!canSaveProjectDefault) return;
                                        try {
                                            setErr(null);
                                            await onSaveAsProjectDefault();
                                        } catch (e: any) {
                                            setErr(e?.message ?? 'Project Settings 저장 실패');
                                        }
                                    }}
                                    disabled={saving || !canSaveProjectDefault}
                                    title={
                                        canSaveProjectDefault
                                            ? '현재 alpha/beta를 프로젝트 기본값(Project Settings)으로 저장합니다.'
                                            : 'Project Owner 또는 Project Manager만 저장할 수 있습니다.'
                                    }
                                >
                                    Project 기본값으로 저장
                                </button>

                                <span className="text-[11px] text-slate-500">
                                    {dbAlpha != null && dbBeta != null ? (
                                        <>
                                            저장됨: α {dbAlpha.toFixed(2)} / β {dbBeta.toFixed(2)}
                                        </>
                                    ) : (
                                        <>저장된 기본값 없음</>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                    {/* alpha, beta 조정 및 Chart 높이 조정 */}
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-1">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-600">alpha (α)</span>
                                <span className="text-xs font-medium text-slate-800">{alpha.toFixed(2)}</span>
                            </div>
                            <input
                                type="range"
                                min={0.8}
                                max={6}
                                step={0.05}
                                value={alpha}
                                onChange={(e) => onChangeAlpha(Number(e.target.value))}
                                className="w-full"
                                // disabled={analysisBlocked.blocked}
                            />
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-600">Chart 높이</span>
                                <span className="text-xs font-medium text-slate-800">{chartHeight}px</span>
                            </div>
                            <input
                                type="range"
                                min={240}
                                max={900}
                                step={10}
                                value={chartHeight}
                                onChange={(e) => onChangeHeight(Number(e.target.value))}
                                className="w-full"
                            />
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-600">beta (β)</span>
                                <span className="text-xs font-medium text-slate-800">{beta.toFixed(2)}</span>
                            </div>
                            <input
                                type="range"
                                min={0.8}
                                max={6}
                                step={0.05}
                                value={beta}
                                onChange={(e) => onChangeBeta(Number(e.target.value))}
                                className="w-full"
                                // disabled={analysisBlocked.blocked}
                            />
                        </div>
                    </div>
                    {/* S-Curve Chart Section */}
                    <div>
                        <div className="mt-2 flex items-center justify-end">
                            <button
                                type="button"
                                className="h-8 px-3 rounded-lg border border-slate-200 text-xs text-slate-700 hover:bg-slate-50"
                                onClick={toggleChartCollapsed}
                                title={chartCollapsed ? '차트 펼치기' : '차트 접기'}
                            >
                                {chartCollapsed ? 'Expand chart' : 'Collapse chart'}
                            </button>
                        </div>
                        {!chartCollapsed ? (
                            <>
                                <SCurveChart
                                    rows={scRows}
                                    height={chartHeight}
                                    showIncrementInTooltip
                                    showLatest={false}
                                    showBenchmark={showBenchmark}
                                    alpha={alpha}
                                    beta={beta}
                                    showFitted={false}
                                    seriesVisible={seriesVisible}
                                    overallDistribution={
                                        overallBellOverlay
                                            ? {
                                                  label: overallBellOverlay.label,
                                                  values: overallBellOverlay.values,
                                                  axis: overallBellOverlay.axis,
                                              }
                                            : undefined
                                    }
                                    selectedDistribution={
                                        selectedBellOverlay
                                            ? {
                                                  label: selectedBellOverlay.label,
                                                  values: selectedBellOverlay.values,
                                                  axis: selectedBellOverlay.axis,
                                              }
                                            : undefined
                                    }
                                    showOverallDistribution={overlayVisible.overallBell !== false}
                                    showSelectedDistribution={overlayVisible.selectedBell !== false}
                                />
                            </>
                        ) : null}
                    </div>
                </div>
                {/* WbsList Section */}
                <div className="flex-1 overflow-auto">
                    <SCurveWbsList
                        source={source}
                        rows={wbsRows}
                        editable={editable && !analysisBlocked.blocked}
                        selectedKey={selectedKey}
                        onSelect={setSelectedKey}
                        onEditField={onEditField}
                        onShift={onShift}
                        onExtendFinish={onExtendFinish}
                        onExtendStart={onExtendStart}
                        originalByKey={source === 'DRAFT' ? originalByKey : undefined}
                        actionStateByKey={source === 'DRAFT' ? actionStateByKey : undefined}
                        onReset={resetDraftWorkItems}
                        canReset={canReset}
                    />
                </div>
            </div>
        </div>
    );
}
